/**
 * SCRUM-1032 QC round 2, item 5 — the station Pipe dropdown in the order the
 * hydraulic tree was built: each mainline followed by its sub-pipes, and theirs,
 * rather than whatever order the dropdown endpoint returns them in.
 *
 * `/api/pipe/dropdown-pipe` answers with a flat list carrying `parentId` and no
 * level, so the tree is rebuilt here. Siblings keep creation (id) order, which
 * is what the Hydraulic step shows.
 */

export interface OrderedPipe {
  id: number
  description: string
  parentId?: number | null
  /** 0 for a mainline, 1 for its sub-pipe, and so on. */
  depth: number
  [key: string]: unknown
}

const parentOf = (pipe: any): number | null => {
  const parent = Number(pipe?.parentId ?? 0)
  return Number.isFinite(parent) && parent > 0 ? parent : null
}

export const orderPipesForDropdown = (pipes: unknown): OrderedPipe[] => {
  const list: any[] = Array.isArray(pipes) ? pipes : []
  const ids = new Set(list.map((pipe) => Number(pipe?.id)))
  const children = new Map<number | null, any[]>()

  list.forEach((pipe) => {
    const parent = parentOf(pipe)
    // A pipe whose parent is not in the list is shown as a root rather than
    // dropped — it is still a pipe the station can use.
    const key = parent !== null && ids.has(parent) ? parent : null
    const siblings = children.get(key) ?? []
    siblings.push(pipe)
    children.set(key, siblings)
  })
  children.forEach((siblings) =>
    siblings.sort((a, b) => Number(a?.id) - Number(b?.id)),
  )

  const ordered: OrderedPipe[] = []
  const seen = new Set<number>()
  const walk = (parent: number | null, depth: number) => {
    for (const pipe of children.get(parent) ?? []) {
      const id = Number(pipe?.id)
      // A parent loop in the data would otherwise recurse forever.
      if (seen.has(id)) continue
      seen.add(id)
      ordered.push({ ...pipe, depth })
      walk(id, depth + 1)
    }
  }
  walk(null, 0)

  // Anything left is only reachable through a loop; list it at the root.
  list.forEach((pipe) => {
    if (!seen.has(Number(pipe?.id))) ordered.push({ ...pipe, depth: 0 })
  })
  return ordered
}

/** The depth of the first pipe with this name, for indenting an option. */
export const pipeDepthByName = (pipes: OrderedPipe[]) => {
  const depths = new Map<string, number>()
  pipes.forEach((pipe) => {
    const name = String(pipe.description ?? '')
    if (!depths.has(name)) depths.set(name, pipe.depth ?? 0)
  })
  return depths
}
